import React, { useEffect, useState, useRef } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Candidate } from '../types/hr.types';
import { caseService } from '../services/caseService';
import { useToast } from './ui/Toast';
import { Mail, Monitor, User, Building, Calendar, CheckCircle2, ExternalLink, Phone, Briefcase, MapPin, UserCheck, Search, FileText, Edit2, Save, Upload, Download, X, CalendarPlus, XCircle, Cake, Clock } from 'lucide-react';
import { formatDate } from '../utils/dateUtils';
interface CandidateModalProps {
  candidate: Candidate | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: (candidate: Candidate) => void;
  onOpenSchedule?: (candidate: Candidate) => void;
}
export function CandidateModal({
  candidate,
  isOpen,
  onClose,
  onUpdate,
  onOpenSchedule
}: CandidateModalProps) {
  const [loadingIT, setLoadingIT] = useState(false);
  const [loadingMkt, setLoadingMkt] = useState(false);
  const [itCaseId, setItCaseId] = useState<string | null>(null);
  const [mktCaseId, setMktCaseId] = useState<string | null>(null);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const [inPersonNotes, setInPersonNotes] = useState('');
  const [desiredStartDate, setDesiredStartDate] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [isEditingInPersonDetails, setIsEditingInPersonDetails] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingResume, setIsUploadingResume] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [isSavingInPerson, setIsSavingInPerson] = useState(false);
  const [isHiring, setIsHiring] = useState(false);
  const {
    addToast
  } = useToast();
  useEffect(() => {
    if (isOpen && candidate) {
      setItCaseId(null);
      setMktCaseId(null);
      setNotes(candidate.notes || '');
      setInPersonNotes(candidate.inPersonInterviewNotes || '');
      setDesiredStartDate(candidate.desiredStartDate || '');
      setDateOfBirth(candidate.dateOfBirth || '');
      setIsEditingNotes(false);
      setIsEditingInPersonDetails(false);
    }
  }, [isOpen, candidate]);
  if (!candidate) return null;
  const fullName = `${candidate.firstName} ${candidate.lastName}`;
  const isInPersonInterview = candidate.status === 'In Person Interview';
  const handleScheduleClick = () => {
    if (onOpenSchedule && candidate) {
      onOpenSchedule(candidate);
      onClose();
    }
  };
  const handleDecline = async () => {
    if (!onUpdate) return;
    setIsDeclining(true);
    await new Promise(r => setTimeout(r, 600));
    onUpdate({
      ...candidate,
      status: 'Declined'
    });
    addToast({
      type: 'info',
      title: 'Candidate Declined',
      message: `${candidate.firstName} ${candidate.lastName} has been marked as declined.`
    });
    setIsDeclining(false);
    onClose();
  };
  const handleSaveNotes = () => {
    if (onUpdate) {
      onUpdate({
        ...candidate,
        notes
      });
      addToast({
        type: 'success',
        title: 'Notes Saved',
        message: 'Candidate notes have been updated.'
      });
    }
    setIsEditingNotes(false);
  };
  const handleSaveInPersonDetails = async () => {
    if (!onUpdate) return;
    setIsSavingInPerson(true);
    await new Promise(r => setTimeout(r, 500));
    onUpdate({
      ...candidate,
      inPersonInterviewNotes: inPersonNotes,
      desiredStartDate: desiredStartDate || undefined,
      dateOfBirth: dateOfBirth || undefined
    });
    addToast({
      type: 'success',
      title: 'Details Saved',
      message: 'Interview details have been updated.'
    });
    setIsSavingInPerson(false);
    setIsEditingInPersonDetails(false);
  };
  const handleResumeUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onUpdate) {
      setIsUploadingResume(true);
      setTimeout(() => {
        onUpdate({
          ...candidate,
          resumeFileName: file.name
        });
        setIsUploadingResume(false);
        addToast({
          type: 'success',
          title: 'Resume Uploaded',
          message: `${file.name} has been attached to this candidate.`
        });
      }, 1000);
    }
  };
  const handleNotifyIT = async () => {
    setLoadingIT(true);
    try {
      const newCase = await caseService.createITCase(candidate.id);
      setItCaseId(newCase.id);
      addToast({
        type: 'success',
        title: 'IT Case Created',
        message: `Case ${newCase.id} has been routed to the IT queue.`
      });
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Action Failed',
        message: error.message || 'Could not create IT case.'
      });
    } finally {
      setLoadingIT(false);
    }
  };
  const handleNotifyMarketing = async () => {
    setLoadingMkt(true);
    try {
      const newCase = await caseService.createMarketingCase(candidate.id);
      setMktCaseId(newCase.id);
      addToast({
        type: 'success',
        title: 'Marketing Case Created',
        message: `Case ${newCase.id} has been routed to the Marketing queue.`
      });
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Action Failed',
        message: error.message || 'Could not create Marketing case.'
      });
    } finally {
      setLoadingMkt(false);
    }
  };
  const handleHireCandidate = async () => {
    if (!onUpdate) return;
    setIsHiring(true);
    await new Promise(r => setTimeout(r, 600));
    onUpdate({
      ...candidate,
      status: 'Offer Signed',
      inPersonInterviewNotes: inPersonNotes,
      desiredStartDate: desiredStartDate || undefined,
      dateOfBirth: dateOfBirth || undefined
    });
    addToast({
      type: 'success',
      title: 'Candidate Hired!',
      message: `${candidate.firstName} ${candidate.lastName} has been moved to Accepted Candidates.`
    });
    setIsHiring(false);
    onClose();
  };
  const handleDenyFromInPerson = async () => {
    if (!onUpdate) return;
    setIsDeclining(true);
    await new Promise(r => setTimeout(r, 600));
    onUpdate({
      ...candidate,
      status: 'Declined',
      inPersonInterviewNotes: inPersonNotes
    });
    addToast({
      type: 'info',
      title: 'Candidate Denied',
      message: `${candidate.firstName} ${candidate.lastName} has been marked as declined.`
    });
    setIsDeclining(false);
    onClose();
  };
  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 text-sm bg-white';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  if (isInPersonInterview) {
    return <Modal isOpen={isOpen} onClose={onClose} title="In Person Interview Details" maxWidth="4xl" data-id="element-131">
        <div className="flex gap-6 h-[70vh]" data-id="element-132">
          <div className="flex-1 overflow-y-auto pr-4 space-y-6" data-id="element-133">
            <div className="flex items-start gap-4" data-id="element-134">
              <div className="h-14 w-14 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center text-blue-700 text-lg font-bold shadow-lg" data-id="element-135">
                {candidate.firstName.charAt(0)}
                {candidate.lastName.charAt(0)}
              </div>
              <div className="flex-1" data-id="element-136">
                <div className="flex items-center gap-3" data-id="element-137">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-138">
                    {fullName}
                  </h3>
                  <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700" data-id="element-139">
                    In Person Interview
                  </span>
                </div>
                <p className="text-gray-500 text-sm" data-id="element-140">
                  {candidate.role} • {candidate.department}
                </p>
              </div>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100" data-id="element-141">
              <div className="flex items-center gap-3" data-id="element-142">
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-143">
                  <CalendarPlus className="h-5 w-5 text-blue-600" data-id="element-144" />
                </div>
                <div data-id="element-145">
                  <p className="text-sm font-medium text-gray-900" data-id="element-146">
                    In Person Interview Scheduled
                  </p>
                  <p className="text-sm text-blue-700" data-id="element-147">
                    {candidate.inPersonInterviewDate ? new Date(candidate.inPersonInterviewDate).toLocaleString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  }) : 'Not scheduled'}
                  </p>
                </div>
              </div>
            </div>
            {onUpdate && <div className="bg-gradient-to-br from-green-50 to-emerald-50/30 rounded-xl p-5 border border-green-100" data-id="element-148">
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3" data-id="element-149">
                  Interview Decision
                </h4>
                <p className="text-sm text-gray-600 mb-4" data-id="element-150">
                  After completing the in-person interview, decide whether to
                  hire or deny this candidate.
                </p>
                <div className="flex gap-3" data-id="element-151">
                  <Button onClick={handleHireCandidate} isLoading={isHiring} variant="primary" className="flex-1 bg-green-600 hover:bg-green-700" leftIcon={<CheckCircle2 className="h-4 w-4" data-id="element-153" />} data-id="element-152">
                    Hire Candidate
                  </Button>
                  <Button onClick={handleDenyFromInPerson} isLoading={isDeclining} variant="ghost" className="flex-1 text-gray-600 hover:text-red-600 hover:bg-red-50 border border-gray-200" leftIcon={<XCircle className="h-4 w-4" data-id="element-155" />} data-id="element-154">
                    Deny Candidate
                  </Button>
                </div>
              </div>}
            <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-156">
              <div className="space-y-3" data-id="element-157">
                <div data-id="element-158">
                  <span className="text-gray-500 text-xs uppercase tracking-wider" data-id="element-159">
                    Email
                  </span>
                  <p className="text-gray-900 flex items-center gap-1.5 mt-0.5" data-id="element-160">
                    <Mail className="h-3.5 w-3.5 text-gray-400" data-id="element-161" />{' '}
                    {candidate.email}
                  </p>
                </div>
                <div data-id="element-162">
                  <span className="text-gray-500 text-xs uppercase tracking-wider" data-id="element-163">
                    Phone
                  </span>
                  <p className="text-gray-900 flex items-center gap-1.5 mt-0.5" data-id="element-164">
                    <Phone className="h-3.5 w-3.5 text-gray-400" data-id="element-165" />{' '}
                    {candidate.phone}
                  </p>
                </div>
                <div data-id="element-166">
                  <span className="text-gray-500 text-xs uppercase tracking-wider" data-id="element-167">
                    Branch
                  </span>
                  <p className="text-gray-900 flex items-center gap-1.5 mt-0.5" data-id="element-168">
                    <MapPin className="h-3.5 w-3.5 text-gray-400" data-id="element-169" />{' '}
                    {candidate.branch}
                  </p>
                </div>
              </div>
              <div className="space-y-3" data-id="element-170">
                <div data-id="element-171">
                  <span className="text-gray-500 text-xs uppercase tracking-wider" data-id="element-172">
                    Interviewer
                  </span>
                  <p className="text-gray-900 flex items-center gap-1.5 mt-0.5" data-id="element-173">
                    <UserCheck className="h-3.5 w-3.5 text-gray-400" data-id="element-174" />{' '}
                    {candidate.interviewedBy}
                  </p>
                </div>
                <div data-id="element-175">
                  <span className="text-gray-500 text-xs uppercase tracking-wider" data-id="element-176">
                    Source
                  </span>
                  <p className="text-gray-900 flex items-center gap-1.5 mt-0.5" data-id="element-177">
                    <Search className="h-3.5 w-3.5 text-gray-400" data-id="element-178" />{' '}
                    {candidate.candidateSource}
                  </p>
                </div>
                <div data-id="element-179">
                  <span className="text-gray-500 text-xs uppercase tracking-wider" data-id="element-180">
                    Phone Screen
                  </span>
                  <p className="text-gray-900 flex items-center gap-1.5 mt-0.5" data-id="element-181">
                    <Calendar className="h-3.5 w-3.5 text-gray-400" data-id="element-182" />{' '}
                    {formatDate(candidate.phoneInterviewDate)}
                  </p>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-100 pt-4" data-id="element-183">
              <div className="flex items-center justify-between mb-4" data-id="element-184">
                <h4 className="font-semibold text-gray-900" data-id="element-185">
                  Interview Details
                </h4>
                {onUpdate && !isEditingInPersonDetails && <Button variant="ghost" size="sm" onClick={() => setIsEditingInPersonDetails(true)} className="text-xs" data-id="element-186">
                    Edit Details
                  </Button>}
                {isEditingInPersonDetails && <div className="flex gap-2" data-id="element-187">
                    <Button variant="ghost" size="sm" onClick={() => setIsEditingInPersonDetails(false)} className="text-xs" data-id="element-188">
                      Cancel
                    </Button>
                    <Button variant="primary" size="sm" onClick={handleSaveInPersonDetails} isLoading={isSavingInPerson} className="text-xs" data-id="element-189">
                      <Save className="h-3 w-3 mr-1" data-id="element-190" /> Save
                    </Button>
                  </div>}
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-191">
                <div data-id="element-192">
                  <label className={labelClass} data-id="element-193">
                    <span className="flex items-center gap-1.5" data-id="element-194">
                      <Cake className="h-3.5 w-3.5 text-gray-400" data-id="element-195" /> Date of
                      Birth
                    </span>
                  </label>
                  {isEditingInPersonDetails ? <input type="date" value={dateOfBirth} onChange={e => setDateOfBirth(e.target.value)} className={inputClass} data-id="element-196" /> : <p className="text-sm text-gray-900 py-2" data-id="element-197">
                      {candidate.dateOfBirth ? new Date(candidate.dateOfBirth).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  }) : <span className="text-gray-400 italic" data-id="element-198">
                          Not provided
                        </span>}
                    </p>}
                </div>
                <div data-id="element-199">
                  <label className={labelClass} data-id="element-200">
                    <span className="flex items-center gap-1.5" data-id="element-201">
                      <Clock className="h-3.5 w-3.5 text-gray-400" data-id="element-202" /> Desired
                      Start Date
                    </span>
                  </label>
                  {isEditingInPersonDetails ? <input type="date" value={desiredStartDate} onChange={e => setDesiredStartDate(e.target.value)} className={inputClass} data-id="element-203" /> : <p className="text-sm text-gray-900 py-2" data-id="element-204">
                      {candidate.desiredStartDate ? new Date(candidate.desiredStartDate).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  }) : <span className="text-gray-400 italic" data-id="element-205">
                          Not provided
                        </span>}
                    </p>}
                </div>
              </div>
              <div className="mt-4" data-id="element-206">
                <label className={labelClass} data-id="element-207">
                  <span className="flex items-center gap-1.5" data-id="element-208">
                    <Edit2 className="h-3.5 w-3.5 text-gray-400" data-id="element-209" /> In Person
                    Interview Notes
                  </span>
                </label>
                {isEditingInPersonDetails ? <textarea value={inPersonNotes} onChange={e => setInPersonNotes(e.target.value)} rows={4} className={`${inputClass} resize-none`} placeholder="Add notes from the in-person interview..." data-id="element-210" /> : <div className="bg-gray-50 rounded-lg p-3 min-h-[80px] text-sm text-gray-600 whitespace-pre-wrap" data-id="element-211">
                    {candidate.inPersonInterviewNotes || <span className="text-gray-400 italic" data-id="element-212">
                        No interview notes yet.
                      </span>}
                  </div>}
              </div>
            </div>
            {candidate.notes && <div className="border-t border-gray-100 pt-4" data-id="element-213">
                <h4 className="font-semibold text-gray-900 mb-2 text-sm" data-id="element-214">
                  Phone Screen Notes
                </h4>
                <div className="bg-amber-50 rounded-lg p-3 text-sm text-gray-600 whitespace-pre-wrap border border-amber-100" data-id="element-215">
                  {candidate.notes}
                </div>
              </div>}
            <div className="flex justify-end pt-4 border-t border-gray-100" data-id="element-216">
              <Button variant="ghost" onClick={onClose} data-id="element-217">
                Close
              </Button>
            </div>
          </div>
          <div className="w-[400px] flex-shrink-0 border-l border-gray-200 pl-6 flex flex-col" data-id="element-218">
            <div className="flex items-center justify-between mb-4" data-id="element-219">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-220">
                <FileText className="h-4 w-4 text-gray-500" data-id="element-221" /> Resume Preview
              </h4>
              {candidate.resumeFileName && <Button variant="ghost" size="sm" className="text-xs" data-id="element-222">
                  <Download className="h-3 w-3 mr-1" data-id="element-223" /> Download
                </Button>}
            </div>
            {candidate.resumeFileName ? <div className="flex-1 bg-gray-100 rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-224">
                <div className="bg-white px-4 py-3 border-b border-gray-200 flex items-center gap-3" data-id="element-225">
                  <div className="h-8 w-8 bg-orange-50 rounded-lg flex items-center justify-center" data-id="element-226">
                    <FileText className="h-4 w-4 text-orange-500" data-id="element-227" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-228">
                    <p className="text-sm font-medium text-gray-900 truncate" data-id="element-229">
                      {candidate.resumeFileName}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-230">PDF Document</p>
                  </div>
                </div>
                <div className="flex-1 p-4 flex items-center justify-center bg-gray-50" data-id="element-231">
                  <div className="w-full h-full bg-white rounded-lg shadow-sm border border-gray-200 p-6 overflow-y-auto" data-id="element-232">
                    <div className="space-y-4" data-id="element-233">
                      <div className="text-center border-b border-gray-100 pb-4" data-id="element-234">
                        <h2 className="text-xl font-bold text-gray-900" data-id="element-235">
                          {fullName}
                        </h2>
                        <p className="text-sm text-gray-500 mt-1" data-id="element-236">
                          {candidate.role}
                        </p>
                        <div className="flex items-center justify-center gap-4 mt-2 text-xs text-gray-500" data-id="element-237">
                          <span data-id="element-238">{candidate.email}</span>
                          <span data-id="element-239">•</span>
                          <span data-id="element-240">{candidate.phone}</span>
                        </div>
                      </div>
                      <div data-id="element-241">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-242">
                          Summary
                        </h3>
                        <div className="h-3 bg-gray-100 rounded w-full mb-1.5" data-id="element-243"></div>
                        <div className="h-3 bg-gray-100 rounded w-11/12 mb-1.5" data-id="element-244"></div>
                        <div className="h-3 bg-gray-100 rounded w-4/5" data-id="element-245"></div>
                      </div>
                      <div data-id="element-246">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-247">
                          Experience
                        </h3>
                        <div className="space-y-3" data-id="element-248">
                          <div data-id="element-249">
                            <div className="h-3 bg-gray-200 rounded w-1/2 mb-1" data-id="element-250"></div>
                            <div className="h-2.5 bg-gray-100 rounded w-1/3 mb-2" data-id="element-251"></div>
                            <div className="h-2.5 bg-gray-100 rounded w-full mb-1" data-id="element-252"></div>
                            <div className="h-2.5 bg-gray-100 rounded w-11/12" data-id="element-253"></div>
                          </div>
                        </div>
                      </div>
                      <div data-id="element-254">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-255">
                          Skills
                        </h3>
                        <div className="flex flex-wrap gap-2" data-id="element-256">
                          <div className="h-6 bg-gray-100 rounded-full w-16" data-id="element-257"></div>
                          <div className="h-6 bg-gray-100 rounded-full w-20" data-id="element-258"></div>
                          <div className="h-6 bg-gray-100 rounded-full w-14" data-id="element-259"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div> : <div className="flex-1 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center" data-id="element-260">
                <FileText className="h-12 w-12 text-gray-300 mb-3" data-id="element-261" />
                <p className="text-sm text-gray-500 mb-3" data-id="element-262">No resume uploaded</p>
                <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} isLoading={isUploadingResume} data-id="element-263">
                  <Upload className="h-3 w-3 mr-1" data-id="element-264" /> Upload Resume
                </Button>
                <input type="file" ref={fileInputRef} className="hidden" accept=".pdf,.doc,.docx" onChange={handleResumeUpload} data-id="element-265" />
              </div>}
          </div>
        </div>
      </Modal>;
  }
  return <Modal isOpen={isOpen} onClose={onClose} title="Candidate Details" maxWidth="lg" data-id="element-266">
      <div className="space-y-8" data-id="element-267">
        <div className="flex items-start gap-4" data-id="element-268">
          <div className="h-16 w-16 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center text-orange-700 text-xl font-bold shadow-lg" data-id="element-269">
            {candidate.firstName.charAt(0)}
            {candidate.lastName.charAt(0)}
          </div>
          <div className="flex-1" data-id="element-270">
            <div className="flex items-center gap-3" data-id="element-271">
              <h3 className="text-xl font-bold text-gray-900" data-id="element-272">{fullName}</h3>
              <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${candidate.status === 'Declined' ? 'bg-gray-100 text-gray-600' : candidate.status === 'Phone Screen' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`} data-id="element-273">
                {candidate.status}
              </span>
            </div>
            <p className="text-gray-500" data-id="element-274">{candidate.role}</p>
            <div className="mt-2 flex flex-wrap gap-3 text-sm text-gray-600" data-id="element-275">
              <span className="flex items-center gap-1" data-id="element-276">
                <Building className="h-4 w-4" data-id="element-277" /> {candidate.department}
              </span>
              <span className="flex items-center gap-1" data-id="element-278">
                <MapPin className="h-4 w-4" data-id="element-279" /> {candidate.branch}
              </span>
              <span className="flex items-center gap-1" data-id="element-280">
                <Calendar className="h-4 w-4" data-id="element-281" /> Phone Screen:{' '}
                {formatDate(candidate.phoneInterviewDate)}
              </span>
              {candidate.inPersonInterviewDate && <span className="flex items-center gap-1 text-blue-600 font-medium" data-id="element-282">
                  <CalendarPlus className="h-4 w-4" data-id="element-283" /> In-Person:{' '}
                  {new Date(candidate.inPersonInterviewDate).toLocaleDateString()}
                </span>}
            </div>
          </div>
        </div>
        {candidate.status === 'Phone Screen' && onUpdate && <div className="bg-gradient-to-br from-amber-50 to-orange-50/30 rounded-xl p-6 border border-amber-100" data-id="element-284">
            <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-285">
              Phone Screen Decision
            </h4>
            <p className="text-sm text-gray-600 mb-4" data-id="element-286">
              After completing the phone screen, decide whether to move this
              candidate forward or decline.
            </p>
            <div className="flex flex-col sm:flex-row gap-3" data-id="element-287">
              <Button onClick={handleScheduleClick} variant="primary" className="flex-1" leftIcon={<CalendarPlus className="h-4 w-4" data-id="element-289" />} data-id="element-288">
                Schedule In Person Interview
              </Button>
              <Button onClick={handleDecline} isLoading={isDeclining} variant="ghost" className="flex-1 text-gray-600 hover:text-red-600 hover:bg-red-50 border border-gray-200" leftIcon={<XCircle className="h-4 w-4" data-id="element-291" />} data-id="element-290">
                Decline Candidate
              </Button>
            </div>
          </div>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" data-id="element-292">
          <div className="p-4 bg-gray-50 rounded-lg space-y-3" data-id="element-293">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-294">
              <User className="h-4 w-4 text-gray-500" data-id="element-295" /> Contact Info
            </h4>
            <div className="space-y-2 text-gray-600" data-id="element-296">
              <div className="flex items-center gap-2" data-id="element-297">
                <Mail className="h-3.5 w-3.5" data-id="element-298" /> {candidate.email}
              </div>
              <div className="flex items-center gap-2" data-id="element-299">
                <Phone className="h-3.5 w-3.5" data-id="element-300" /> {candidate.phone}
              </div>
            </div>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg space-y-3" data-id="element-301">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-302">
              <Briefcase className="h-4 w-4 text-gray-500" data-id="element-303" /> Recruitment Info
            </h4>
            <div className="space-y-2 text-gray-600" data-id="element-304">
              <div className="flex items-center gap-2" data-id="element-305">
                <UserCheck className="h-3.5 w-3.5" data-id="element-306" /> Interviewer:{' '}
                {candidate.interviewedBy}
              </div>
              <div className="flex items-center gap-2" data-id="element-307">
                <Search className="h-3.5 w-3.5" data-id="element-308" /> Source:{' '}
                {candidate.candidateSource}
                {candidate.referredBy && <span className="text-xs bg-gray-200 px-1.5 py-0.5 rounded" data-id="element-309">
                    Ref: {candidate.referredBy}
                  </span>}
                {candidate.socialMediaPlatform && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded" data-id="element-310">
                    {candidate.socialMediaPlatform}
                  </span>}
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6" data-id="element-311">
          <div className="border-t border-gray-100 pt-6" data-id="element-312">
            <div className="flex items-center justify-between mb-4" data-id="element-313">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-314">
                <FileText className="h-4 w-4 text-gray-500" data-id="element-315" /> Resume / CV
              </h4>
              {!candidate.resumeFileName && <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} isLoading={isUploadingResume} className="text-xs" data-id="element-316">
                  <Upload className="h-3 w-3 mr-1" data-id="element-317" /> Upload
                </Button>}
              <input type="file" ref={fileInputRef} className="hidden" accept=".pdf,.doc,.docx" onChange={handleResumeUpload} data-id="element-318" />
            </div>
            {candidate.resumeFileName ? <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-319">
                <div className="flex items-center gap-3" data-id="element-320">
                  <div className="h-10 w-10 bg-white rounded-lg border border-gray-200 flex items-center justify-center text-orange-500" data-id="element-321">
                    <FileText className="h-5 w-5" data-id="element-322" />
                  </div>
                  <div data-id="element-323">
                    <p className="text-sm font-medium text-gray-900" data-id="element-324">
                      {candidate.resumeFileName}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-325">Uploaded document</p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-326">
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-id="element-327">
                    <Download className="h-4 w-4 text-gray-500" data-id="element-328" />
                  </Button>
                  {onUpdate && <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => onUpdate({
                ...candidate,
                resumeFileName: undefined
              })} data-id="element-329">
                      <X className="h-4 w-4" data-id="element-330" />
                    </Button>}
                </div>
              </div> : <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300" data-id="element-331">
                <p className="text-sm text-gray-500" data-id="element-332">No resume uploaded</p>
              </div>}
          </div>
          <div className="border-t border-gray-100 pt-6" data-id="element-333">
            <div className="flex items-center justify-between mb-4" data-id="element-334">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-335">
                <Edit2 className="h-4 w-4 text-gray-500" data-id="element-336" /> Notes
              </h4>
              {onUpdate && !isEditingNotes && <Button variant="ghost" size="sm" onClick={() => setIsEditingNotes(true)} className="text-xs" data-id="element-337">
                  Edit Notes
                </Button>}
              {isEditingNotes && <div className="flex gap-2" data-id="element-338">
                  <Button variant="ghost" size="sm" onClick={() => setIsEditingNotes(false)} className="text-xs" data-id="element-339">
                    Cancel
                  </Button>
                  <Button variant="primary" size="sm" onClick={handleSaveNotes} className="text-xs" data-id="element-340">
                    <Save className="h-3 w-3 mr-1" data-id="element-341" /> Save
                  </Button>
                </div>}
            </div>
            {isEditingNotes ? <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 text-sm bg-white resize-none" placeholder="Add notes about this candidate..." autoFocus data-id="element-342" /> : <div className="bg-gray-50 rounded-lg p-4 min-h-[100px] text-sm text-gray-600 whitespace-pre-wrap" data-id="element-343">
                {candidate.notes || 'No notes added yet.'}
              </div>}
          </div>
        </div>
        {(candidate.status === 'Offer Signed' || candidate.status === 'Pre-boarding' || candidate.status === 'Ready to Start') && <div className="bg-gradient-to-br from-gray-50 to-orange-50/30 rounded-xl p-6 border border-gray-100 mt-6" data-id="element-344">
            <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-345">
              Onboarding Actions
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-346">
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex flex-col" data-id="element-347">
                <div className="flex items-center gap-2 mb-2" data-id="element-348">
                  <Monitor className="h-5 w-5 text-orange-600" data-id="element-349" />
                  <span className="font-medium text-gray-900" data-id="element-350">IT Setup</span>
                </div>
                <p className="text-xs text-gray-500 mb-4 flex-1" data-id="element-351">
                  Provisions accounts (MS, Salesforce), email, and hardware.
                </p>
                {itCaseId ? <div className="mt-auto bg-green-50 text-green-700 px-3 py-2 rounded text-sm flex items-center gap-2" data-id="element-352">
                    <CheckCircle2 className="h-4 w-4" data-id="element-353" />
                    <span className="flex-1 truncate" data-id="element-354">Case #{itCaseId}</span>
                    <ExternalLink className="h-3 w-3 opacity-50" data-id="element-355" />
                  </div> : <Button onClick={handleNotifyIT} isLoading={loadingIT} variant="primary" className="w-full mt-auto" data-id="element-356">
                    Notify IT
                  </Button>}
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex flex-col" data-id="element-357">
                <div className="flex items-center gap-2 mb-2" data-id="element-358">
                  <User className="h-5 w-5 text-orange-600" data-id="element-359" />
                  <span className="font-medium text-gray-900" data-id="element-360">
                    Marketing Setup
                  </span>
                </div>
                <p className="text-xs text-gray-500 mb-4 flex-1" data-id="element-361">
                  Requests headshots, building badge, and swag pack.
                </p>
                {mktCaseId ? <div className="mt-auto bg-green-50 text-green-700 px-3 py-2 rounded text-sm flex items-center gap-2" data-id="element-362">
                    <CheckCircle2 className="h-4 w-4" data-id="element-363" />
                    <span className="flex-1 truncate" data-id="element-364">Case #{mktCaseId}</span>
                    <ExternalLink className="h-3 w-3 opacity-50" data-id="element-365" />
                  </div> : <Button onClick={handleNotifyMarketing} isLoading={loadingMkt} variant="primary" className="w-full mt-auto" data-id="element-366">
                    Notify Marketing
                  </Button>}
              </div>
            </div>
          </div>}
        <div className="flex justify-end pt-4 border-t border-gray-100" data-id="element-367">
          <Button variant="ghost" onClick={onClose} data-id="element-368">
            Close
          </Button>
        </div>
      </div>
    </Modal>;
}